/**
 * Reports whether text content is taller than the clamp allows, so the
 * "Show more" toggle only appears when the clamp actually hides something.
 *
 * Measured against the line-height rather than the element's rendered height
 * so the answer stays the same whether the text is currently collapsed or
 * expanded. Half a line of slack absorbs inline elements that make a line box
 * a few px taller than the line-height (e.g. a padded slash-command tag), so
 * a message that fits in `maxLines` never gets a toggle that reveals nothing.
 *
 * @param {number} contentHeight - Full height of the text (`scrollHeight`), in px
 * @param {number} lineHeight - Computed line-height of the text, in px
 * @param {number} maxLines - Lines shown while collapsed
 * @returns {boolean} True when the text needs more than `maxLines` lines; false
 *   when it fits or when either measurement is unusable (e.g. not laid out yet)
 */
const isTextOverflowingClamp = (contentHeight: number, lineHeight: number, maxLines: number): boolean => {
  if (!Number.isFinite(contentHeight) || !Number.isFinite(lineHeight) || lineHeight <= 0) return false;
  return contentHeight > lineHeight * (maxLines + 0.5);
};

/**
 * Browsers' `line-height: normal` is roughly this multiple of the font size
 * (font-dependent; 1.2 is the common value and what CSS recommends).
 */
const NORMAL_LINE_HEIGHT_RATIO = 1.2;

/**
 * Resolves an element's line-height in px, including `normal`, which computes
 * to the keyword rather than a length and would otherwise parse to NaN and
 * silently turn the clamp off.
 *
 * @param {Pick<CSSStyleDeclaration, 'lineHeight' | 'fontSize'>} style - The element's computed style
 * @returns {number} Line-height in px, or NaN when neither value is measurable
 */
const resolveLineHeightPx = (style: Pick<CSSStyleDeclaration, 'lineHeight' | 'fontSize'>): number => {
  const lineHeight = Number.parseFloat(style.lineHeight);
  if (Number.isFinite(lineHeight)) return lineHeight;
  return Number.parseFloat(style.fontSize) * NORMAL_LINE_HEIGHT_RATIO;
};

export { isTextOverflowingClamp, resolveLineHeightPx };
